import { useQuery } from '@tanstack/react-query';
import { Alert, Card, Col, List, Row, Skeleton, Statistic, Typography } from 'antd';
import dayjs from 'dayjs';

import { useSession } from '../features/auth/useSession';
import { apiClient } from '../lib/api-client';
import { useAuthStore } from '../shared/auth/authStore';

interface DashboardWidget {
  label: string;
  amount: string;
}

interface DashboardData {
  widgets: DashboardWidget[];
}

interface WorkflowTask {
  id: string;
  docType?: string;
  entityId?: string;
  status?: string;
}

export function DashboardPage(): JSX.Element {
  const { data: user } = useSession();
  const activeCompanyId = useAuthStore((state) => state.activeCompanyId);
  const dashboard = useQuery({
    queryKey: ['dashboard', activeCompanyId],
    enabled: activeCompanyId !== null,
    queryFn: async () => {
      const { data } = await apiClient.get<DashboardData>('/rp/dashboard', {
        params: { date: dayjs().format('YYYY-MM-DD') },
      });
      return (data as { data?: DashboardData }).data ?? data;
    },
  });
  const inbox = useQuery({
    queryKey: ['workflow', 'inbox'],
    queryFn: async () => {
      const { data } = await apiClient.get<WorkflowTask[]>('/workflow/inbox');
      return (data as { data?: WorkflowTask[] }).data ?? data;
    },
  });

  return (
    <div>
      <Typography.Title level={4}>Xin chào, {user?.fullName ?? 'bạn'} 👋</Typography.Title>
      <Typography.Paragraph type="secondary">
        Số liệu tài chính tại ngày {dayjs().format('DD/MM/YYYY')} của công ty đang chọn.
      </Typography.Paragraph>
      {!activeCompanyId && <Alert type="info" showIcon message="Hãy chọn công ty để xem số liệu tài chính." />}
      <Row gutter={[16, 16]}>
        {(dashboard.data?.widgets ?? []).map((widget) => (
          <Col xs={24} sm={12} lg={6} key={widget.label}>
            <Card loading={dashboard.isLoading}>
              <Statistic
                title={widget.label}
                value={Number(widget.amount)}
                precision={0}
                groupSeparator="."
                decimalSeparator=","
              />
            </Card>
          </Col>
        ))}
        {!dashboard.data?.widgets.length && (
          <>
            <Col xs={24} sm={12} lg={6}>
              <Card><Statistic title="Công ty trong phạm vi" value={user?.companyScope.length ?? 0} /></Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card><Statistic title="Việc cần duyệt" value={inbox.data?.length ?? 0} /></Card>
            </Col>
          </>
        )}
        <Col xs={24} lg={12}>
          <Card title="Việc cần duyệt">
            {inbox.isLoading ? (
              <Skeleton active paragraph={{ rows: 3 }} />
            ) : (
              <List
                dataSource={inbox.data ?? []}
                locale={{ emptyText: 'Không có việc chờ duyệt' }}
                renderItem={(task) => (
                  <List.Item>
                    <List.Item.Meta
                      title={task.docType ?? 'Chứng từ'}
                      description={task.entityId ?? task.id}
                    />
                  </List.Item>
                )}
              />
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
}
